<template>
	<hr class="white-fade" />
	<div class="booking-group">
		<div class="input-holder wide">
			<h4 class="text-uppercase">Total</h4>
			<h3>
				{{ formatCurrency(total) }} <small class="text-uppercase dm-sans" v-if="domesticInternational === 'Domestic'">Incl. {{ formatCurrency(gst) }} GST</small>
			</h3>
		</div>
	</div>
	<hr class="white-fade" />

	<div class="generic-form">
		<form @submit.prevent="submit" novalidate>
			<div class="input-holder wide has-dropdown">
				<label for="PaymentType">Payment Type*</label>
				<div class="select-holder">
					<Select id="PaymentType" name="PaymentType" v-model="request.PaymentType" option-value="value" option-label="label" :options="paymentTypes" placeholder="Select your Payment Type" show-clear></Select>
				</div>
				<small class="p-error" v-if="form.PaymentType.$error">{{ form.PaymentType.$errors[0].$message }}</small>
			</div>

			<div class="form-button">
				<Button type="submit" class="btn" :loading="isSubmitting || isSubmitted"> {{ request.PaymentType === 'BankTransfer' ? 'Book now' : 'Pay Securely Now' }}</Button>
			</div>

			<p class="text-mid-grey payment-subtext" :class="[{ 'cc-visible': (request.PaymentType === 'CreditCard' || request.PaymentType === 'CreditCardPxFusion') }]">A 2.5% surcharge applies to all credit card payments.</p>
		</form>
	</div>
</template>
<script lang="ts" setup>
	import type { ApiResult } from '@/types/ApiResult';
	import type { DomesticInternational } from '@/types/BookingGameMetadataModel';
	import useVuelidate from '@vuelidate/core';
	import { helpers, required } from '@vuelidate/validators';
	import { Select } from 'primevue';
	import { computed, ref, type PropType } from 'vue';
	import wretch from 'wretch';
	import { formatCurrency } from './formatters';
	import type { BookingPaymentRequestModel, PaymentType, RedirectUrlModel } from './types';

	const props = defineProps({
		bookingId: { type: String, required: true },
		paymentType: { type: String as PropType<PaymentType> },
		domesticInternational: { type: String as PropType<DomesticInternational> },
		gst: { type: Number, required: true },
		total: { type: Number, required: true },
		creditCardFee: { type: Number, required: true },
		enableBankTransfer: Boolean,
		enableCreditCard: Boolean,
		enableCreditCardPxFusion: Boolean,
	});

	const isSubmitting = ref<boolean>(false);
	const isSubmitted = ref<boolean>(false);
	const paymentTypes = computed<{ value: PaymentType; label: string }[]>(() => {
		const result: { value: PaymentType; label: string }[] = [];
		if (props.enableBankTransfer) {
			result.push({ value: 'BankTransfer', label: 'Bank transfer' });
		}
		if (props.enableCreditCard) {
			result.push({ value: 'CreditCard', label: 'Credit card' });
		}
		if (props.enableCreditCardPxFusion) {
			result.push({ value: 'CreditCardPxFusion', label: 'Credit card' });
		}
		return result;
	});

	const rules = computed<Partial<Record<keyof BookingPaymentRequestModel, unknown>>>(() => ({
		PaymentType: {
			required: helpers.withMessage('Required', required),
		},
	}));
	const request = ref<BookingPaymentRequestModel>({
		BookingId: props.bookingId,
		PaymentType: props.paymentType ? props.paymentType : undefined,
	});
	const form = useVuelidate<BookingPaymentRequestModel>(rules, request);

	async function submit() {
		if (!(await form.value.$validate())) {
			return;
		}

		try {
			isSubmitting.value = true;
			const resp = await wretch('/api/booking/start-payment').post(request.value).json<ApiResult<RedirectUrlModel>>();
			if (resp.successful && resp.data) {
				window.location.href = resp.data.RedirectUrl;
				isSubmitted.value = true;
			}
		} finally {
			isSubmitting.value = false;
		}
	}
</script>
